import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { useUser } from './context/UserContext';
import Login from './pages/Login';
import SetupUsername from './pages/SetupUsername';
import Dashboard from './pages/Dashboard';
import Course from './pages/Course';
import CreateCourse from './pages/CreateCourse';
import EditCourse from './pages/EditCourse';
import Settings from './pages/Settings';
import StudySession from './pages/StudySession';
import LessonView from './pages/LessonView';

export default function App() {
  const { isLoggedIn, token, authError, isGuest } = useAuth();
  // Fetched once by UserProvider when the token arrives; shared app-wide.
  const { user, userError, setUser } = useUser();

  if (!isLoggedIn && !isGuest) return <Login />;

  // Authenticated users: wait for /me and ensure a username is set. Guests bypass
  // both — they have no account and cannot create or edit courses.
  if (!isGuest) {
    // Surface a failure instead of rendering a blank screen. A null user can mean
    // the token never arrived (e.g. iOS Safari silent-auth failure) or /me errored;
    // both previously left the user staring at a permanent white page.
    if (authError || userError) {
      const message = (authError || userError).message || 'Please try signing in again.';
      return (
        <div className="min-h-screen bg-amber-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl shadow-md p-6 sm:p-8 w-full max-w-md text-center">
            <p className="text-amber-700 text-lg font-semibold mb-2">We couldn't sign you in</p>
            <p className="text-amber-600 text-sm mb-6">{message}</p>
            <button
              onClick={() => window.location.reload()}
              className="w-full rounded-lg px-4 py-3 font-medium text-white bg-amber-500 hover:bg-amber-600 transition"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    if (!user) {
      return (
        <div className="min-h-screen bg-amber-50 flex items-center justify-center">
          <p className="text-amber-600 text-lg">Loading your hive...</p>
        </div>
      );
    }

    if (!user.username) {
      return <SetupUsername token={token} onComplete={setUser} />;
    }
  }

  const isAdmin = !!user?.admin;
  // Account-bound pages bounce guests home; browsing/studying stays open to them.
  const authedOnly = (element) => (isGuest ? <Navigate to="/" replace /> : element);

  return (
    <Routes>
      <Route path="/" element={<Dashboard isGuest={isGuest} />} />
      <Route path="/settings" element={authedOnly(<Settings />)} />
      <Route path="/create" element={authedOnly(<CreateCourse />)} />
      <Route path="/courses/:courseId" element={<Course isAdmin={isAdmin} />} />
      <Route path="/courses/:courseId/edit" element={authedOnly(<EditCourse />)} />
      <Route path="/courses/:courseId/lessons/:lessonId" element={<LessonView isAdmin={isAdmin} />} />
      <Route path="/courses/:courseId/study/:mode" element={<StudySession />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
